import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { FunctionComponent, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import LoaderButton from '../../../components/common/LoaderButton';
import Input from '../../../components/Input';
import Label from '../../../components/Label';
import { useAppDispatch, useAppSelector } from '../../../store';
import { clearAuthState, clearCognitoStorage, generateMagicLinkThunk } from '../api/authSlice';
import { LoginMagicLinkSchema } from '../schemas';
import alert from '../../../utils/alert';

type LoginFormData = {
  email: string;
};

export const LoginMagicLink: FunctionComponent = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, dirtyFields },
  } = useForm<LoginFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(LoginMagicLinkSchema),
  });

  const { t }: any = useTranslation();
  const dispatch = useAppDispatch();

  const auth = useAppSelector((state) => state.auth);
  const [isLinkSent, setIsLinkSent] = useState(false);

  useEffect(() => {
    clearCognitoStorage();
  }, []);

  const onSubmit = async (data: LoginFormData) => {
    const { email } = data;

    try {
      const res = await dispatch(generateMagicLinkThunk({ email: email })).unwrap();
      if (res.data.statusCode === 200) {
        alert.success(t('Link sent successfully!'), { toastId: 'magiclink-sent-success' });
        setIsLinkSent(true);
      }
    } catch (error: any) {
      if (error.message === 'Magic link already requested') {
        alert.error(t('Login link already requested. Please check your inbox.'));
      } else {
        alert.error(t('Something went wrong. Please try again.'), {
          toastId: 'magiclink-error',
        });
      }
    }
  };

  useEffect(() => {
    dispatch(clearAuthState());
  }, [dispatch]);

  return (
    <div>
      {!isLinkSent ? (
        <>
          <div className='d-flex flex-column align-items-start'>
            <h2 className='auth__page-title'>Single login link</h2>
            <p className='auth__help-text'>
              Please enter your details, we will send you an email with your login link!
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className={'pt-2'}>
            <Form.Group controlId='email' className='auth__input-group'>
              <Label>{t('Email')}</Label>
              <Input
                type='text'
                placeholder={t('Email')}
                className={clsx(
                  [errors.email && 'is-invalid'],
                  [
                    dirtyFields.email && !errors.email
                      ? 'is-valid auth__input-no-bootstrap-icon'
                      : '',
                  ],
                )}
                {...register('email')}
              />
              {errors.email && <div className='invalid-feedback'>{errors.email.message}</div>}
            </Form.Group>
            <div className='d-flex flex-column align-items-center'>
              <LoaderButton
                className='auth__btn-primary'
                type='submit'
                onClick={handleSubmit(onSubmit)}
                isSubmitting={auth.isLoading}
                disabled={!isValid}
              >
                Send login link
              </LoaderButton>
            </div>
          </form>
        </>
      ) : (
        <div className='d-flex flex-column align-items-start'>
          <h2 className='auth__page-title'>Check your inbox</h2>
          <p className='auth__help-text'>
            Please check your inbox and follow the instructions to continue.
          </p>
        </div>
      )}
    </div>
  );
};
